import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type { OfferLanguageItem } from '@recruitee/offers-types';

import { OfferLanguagesSetupEntity } from './job-templates-languages-store.types';

const {
  namespace,
  selectors: defaultSelectors,
  actions: defaultActions,
  reducer,
} = createEntityCollectionStore<OfferLanguagesSetupEntity>()('JobTemplatesLanguagesSetups', {
  idSelector: e => e.offerTemplateId,
  customActions: adapter => ({
    fetchOne: (state, payload: { offerTemplateId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.offerTemplateId] }),
    fetchOneSuccess: (state, payload: { scope: null; data: OfferLanguagesSetupEntity }) =>
      pipeState(adapter.upsertOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.data.offerTemplateId] }),
      ),
    toggle: (state, payload: { offerTemplateId: number; code: string; enabled: boolean }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offerTemplateId] })),
    toggleSuccess: (state, payload: { data: OfferLanguageItem; offerTemplateId: number }) => {
      let updatedEntity = state.entityMap[payload.offerTemplateId];
      if (!updatedEntity) {
        throw new Error(
          `Trying to update non existing OfferLanguagesSetupEntity w offerId ${payload.offerTemplateId}`,
        );
      }
      updatedEntity = { ...updatedEntity };
      const updatedSetup = { ...updatedEntity.setup };
      updatedSetup.languages = updatedSetup.languages.map(language => {
        if (language.code === payload.data.code) return payload.data;
        return language;
      });
      updatedEntity.setup = updatedSetup;

      return pipeState(adapter.updateOne(state, updatedEntity), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [updatedEntity!.offerTemplateId] }),
      );
    },
    toggleFailed: (state, payload: { offerTemplateId: number; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.offerTemplateId] }),
    setAsPrimary: (state, payload: { offerTemplateId: number; code: string }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offerTemplateId] })),
    setAsPrimarySuccess: ngrxEvent<{ offerTemplateId: number; code: string }>(),
    setAsPrimaryFailed: (state, payload: { offerTemplateId: number; error: HttpErrorResponse }) =>
      pipeState(
        adapter.setError(state, payload.error, {
          scope: null,
          entities: [payload.offerTemplateId],
        }),
      ),
  }),
});

const selectors = {
  byOfferTemplateId: memoizeSelector((id: number) =>
    createSelector(
      defaultSelectors.byId(id),
      (state: OfferLanguagesSetupEntity | null) => state?.setup || null,
    ),
  ),
};

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  fetchOneFailed: defaultActions.fetchOneFailed,
  toggle: defaultActions.toggle,
  toggleSuccess: defaultActions.toggleSuccess,
  toggleFailed: defaultActions.toggleFailed,
  setAsPrimary: defaultActions.setAsPrimary,
  setAsPrimarySuccess: defaultActions.setAsPrimarySuccess,
  setAsPrimaryFailed: defaultActions.setAsPrimaryFailed,
};

export {
  actions as JobTemplatesLanguagesActions,
  namespace as JobTemplatesLanguagesNamespace,
  selectors as JobTemplatesLanguagesSelectors,
  reducer as JobTemplatesLanguagesReducer,
};
